import _extends from "@babel/runtime/helpers/esm/extends";
import React, { useRef, forwardRef, useState } from 'react';
import ClassNames from 'classnames';
import { getText, KEY_VALUES, useControlled, getActiveElement } from '../utils';
import { Tooltip } from '../tooltip';
import { DropdownButton } from '../dropdown';
import { useLocalization } from '@appkit4/react-api';
var Tabs = /*#__PURE__*/forwardRef(function (props, ref) {
  var _ClassNames, _ClassNames2, _clickToScrollLabel, _selected3, _clickToScrollLabel2;
  var id = props.id,
    activeIndex = props.activeIndex,
    _props$defaultActiveI = props.defaultActiveIndex,
    defaultActiveIndex = _props$defaultActiveI === void 0 ? 0 : _props$defaultActiveI,
    _props$type = props.type,
    type = _props$type === void 0 ? 'underline' : _props$type,
    children = props.children,
    _props$style = props.style,
    style = _props$style === void 0 ? {} : _props$style,
    className = props.className,
    onTabChange = props.onTabChange,
    _props$responsive = props.responsive,
    responsive = _props$responsive === void 0 ? false : _props$responsive,
    _props$showBothIndica = props.showBothIndicators,
    showBothIndicators = _props$showBothIndica === void 0 ? false : _props$showBothIndica,
    speed = props.speed,
    _props$pinActiveTab = props.pinActiveTab,
    pinActiveTab = _props$pinActiveTab === void 0 ? false : _props$pinActiveTab,
    _props$tabsetId = props.tabsetId,
    tabsetId = _props$tabsetId === void 0 ? String.fromCharCode(65 + Math.floor(Math.random() * 26)) + Date.now() : _props$tabsetId,
    _props$stretched = props.stretched,
    stretched = _props$stretched === void 0 ? false : _props$stretched,
    _props$overflow = props.overflow,
    overflow = _props$overflow === void 0 ? false : _props$overflow,
    _props$overflowNumber = props.overflowNumber,
    overflowNumber = _props$overflowNumber === void 0 ? 8 : _props$overflowNumber;
  var arrowLeft = false;
  var arrowRight = true;
  if (showBothIndicators) {
    arrowLeft = true;
  }
  var _useState = useState(null),
    activeItem = _useState[0],
    setActiveItem = _useState[1];
  var _useState2 = useState(true),
    allowStrech = _useState2[0],
    setAllowStrech = _useState2[1];
  var tabRef = useRef(null);
  var resultsRef = useRef([]);
  var tabsRef = useRef([]);
  var responsiveTab = useRef(null);
  var scrollContainer = useRef(null);
  var leftCtrlDirection = useRef('left');
  var rightCtrlDirection = useRef('right');
  var _useControlled = useControlled(activeIndex, defaultActiveIndex),
    curActiveIndex = _useControlled[0],
    setCurActiveIndex = _useControlled[1];
  var tabLabelPosition = useRef([]);
  var scrollDistance = useRef(0);
  var overflowMode = type === 'filled' && overflow;
  var tabIds = [];
  var _useLocalization = useLocalization(),
    t = _useLocalization.t;
  var onkeydownLeft = function onkeydownLeft(event) {
    var key = event.key;
    var isEnter = key === KEY_VALUES.ENTER;
    isEnter && onClickLeft(event);
  };
  var onkeydownRight = function onkeydownRight(event) {
    var key = event.key;
    var isEnter = key === KEY_VALUES.ENTER;
    isEnter && onClickRight(event);
  };
  React.useEffect(function () {
    var initEvent = function initEvent() {
      if (responsive) {
        var _scrollContainer$curr;
        setTimeout(function () {
          initTabLabelPosition();
          setTabAttribute();
          initResponsiveTab();
          setController();
        }, 0);
        window.addEventListener('resize', initEvent);
        (_scrollContainer$curr = scrollContainer.current) === null || _scrollContainer$curr === void 0 ? void 0 : _scrollContainer$curr.addEventListener('scroll', function () {
          scrollDistance.current = scrollContainer.current.scrollLeft;
          setController();
        });
      }
    };
    initEvent();
  }, [responsive, t("aria")]);
  React.useEffect(function () {
    if (responsive && children && children.length > 0 && children[activeIndex]) {
      setActiveItem(children[activeIndex].props);
    }
  }, [children]);
  var _onKeyDown = function onKeyDown(event, child) {
    var _tabRef$current;
    var KEY_LEFT_DOWN = 37;
    var KEY_RIGHT_DOWN = 39;
    var tabList = (_tabRef$current = tabRef.current) === null || _tabRef$current === void 0 ? void 0 : _tabRef$current.querySelectorAll('.ap-tabset-toggle');
    var index = curActiveIndex;
    if (!children) return;
    if (responsive && pinActiveTab) {
      if (event.keyCode === 13) {
        var _activeElement$getAtt;
        var activeElement = getActiveElement();
        index = Array.prototype.indexOf.call(tabsRef.current.map(function (item) {
          return item.label || item.icon;
        }), activeElement === null || activeElement === void 0 ? void 0 : (_activeElement$getAtt = activeElement.getAttribute('id')) === null || _activeElement$getAtt === void 0 ? void 0 : _activeElement$getAtt.replace('tab-', ''));
        var focusIndex = Array.prototype.indexOf.call(tabList, activeElement);
        selectTab(null, index, child.disabled, child, focusIndex);
      }
      resultsRef.current[index] = child;
    } else {
      if (overflowMode && curActiveIndex >= overflowNumber) {
        if (event.keyCode === 13) {
          setCurActiveIndex(0);
          selectTab(null, 0, false, child);
        }
      } else {
        if (event.keyCode === KEY_LEFT_DOWN) {
          event.preventDefault();
          index = findNextAvailableTabIndex('left', index);
          setCurActiveIndex(index);
          (tabList === null || tabList === void 0 ? void 0 : tabList[index]).focus({
            preventScroll: true
          });
          selectTab(null, index, false, child);
        } else if (event.keyCode === KEY_RIGHT_DOWN) {
          event.preventDefault();
          index = findNextAvailableTabIndex('right', index);
          setCurActiveIndex(index);
          (tabList === null || tabList === void 0 ? void 0 : tabList[index]).focus({
            preventScroll: true
          });
          selectTab(null, index, false, child);
        }
      }
    }
  };
  var findNextAvailableTabIndex = function findNextAvailableTabIndex(moveDirection, currentIndex) {
    var tabsArr = [];
    React.Children.forEach(children, function (child, index) {
      if (!child && !child.props) {
        return;
      }
      if (!overflowMode || index < overflowNumber) {
        tabsArr.push(child.props);
      }
    });
    var savedIndex = currentIndex;
    while (true) {
      if (moveDirection === 'left') {
        currentIndex--;
      } else {
        currentIndex++;
      }
      if (currentIndex === savedIndex) break;
      if (moveDirection === 'left' && currentIndex < 0) {
        currentIndex = tabsArr.length - 1;
      }
      if (moveDirection === 'right' && currentIndex === tabsArr.length) {
        currentIndex = 0;
      }
      if (!tabsArr[currentIndex].disabled) {
        break;
      }
    }
    return currentIndex;
  };
  var selectTab = function selectTab(e, index, disabled, child, focusIndex) {
    var _tabRef$current2;
    if (disabled) {
      return;
    }
    var tabItems = (_tabRef$current2 = tabRef.current) === null || _tabRef$current2 === void 0 ? void 0 : _tabRef$current2.querySelectorAll('.ap-tabset-toggle');
    if (focusIndex === -1) {
      focusIndex = Array.prototype.indexOf.call(tabItems, e === null || e === void 0 ? void 0 : e.target);
    }
    setCurActiveIndex(index);
    onTabChange === null || onTabChange === void 0 ? void 0 : onTabChange(index, child.value);
    if (responsive) {
      initTabLabelPosition();
      if (!pinActiveTab && (!overflowMode || index < overflowNumber)) {
        // Scroll to show full label if the selected tab is partially covered by the left border
        if (tabLabelPosition.current[index] < scrollDistance.current) {
          scrollContainer.current.scroll({
            left: tabLabelPosition.current[index],
            top: 0,
            behavior: 'smooth'
          });
        }

        // // Scroll to show full label if the selected tab is partially covered by the right border
        var contianerWidth = scrollContainer.current.offsetWidth;
        var currentTabWidth = tabItems ? Array.from(tabItems)[index].offsetWidth : 0;
        var scrollRight = tabLabelPosition.current[index] - scrollDistance.current + currentTabWidth - contianerWidth;
        if (scrollRight > 0) {
          scrollContainer.current.scroll({
            left: scrollDistance.current + scrollRight,
            top: 0,
            behavior: 'smooth'
          });
        }
      } else {
        setActiveItem(child);
        setTimeout(function () {
          var _tabRef$current3;
          if (!focusIndex && focusIndex !== 0 || focusIndex === -1) return;
          var tab = resultsRef.current[focusIndex];
          var nextFocusTab = (_tabRef$current3 = tabRef.current) === null || _tabRef$current3 === void 0 ? void 0 : _tabRef$current3.querySelector("[id^=tab" + (tab.label ? '-' + fillSpace(tab.label) : tab.icon ? tab.icon : '') + "]");
          nextFocusTab === null || nextFocusTab === void 0 ? void 0 : nextFocusTab.focus({
            preventScroll: true
          });
          setTabAttribute();
        }, 0);
      }
    }
  };
  var fillSpace = function fillSpace(text) {
    var reg = new RegExp(' ', 'g');
    return text.replace(reg, '-');
  };
  var setTabAttribute = function setTabAttribute() {
    var tabs = tabsRef.current;
    if (tabs && tabs.length) {
      tabs.forEach(function (tab, i) {
        setTimeout(function () {
          var _responsiveTab$curren;
          var element = (_responsiveTab$curren = responsiveTab.current) === null || _responsiveTab$curren === void 0 ? void 0 : _responsiveTab$curren.querySelector("[id^=tab" + (tab.label ? '-' + fillSpace(tab.label) : tab.icon ? tab.icon : '') + "]");
          if (element) {
            var ariaLabel = '';
            if (element.classList.contains('pinned')) {
              var _selected, _selected2;
              ariaLabel = tab.icon && !tab.label ? ((_selected = t('aria').selected) === null || _selected === void 0 ? void 0 : _selected.replace("{item}", tab.icon)) || '' : ((_selected2 = t('aria').selected) === null || _selected2 === void 0 ? void 0 : _selected2.replace("{item}", tab.label)) || '';
            } else {
              ariaLabel = tab.icon && !tab.label ? tab.icon : tab.label;
            }
            if (tab.icon && !tab.label || element.classList.contains('pinned')) {
              element.setAttribute('aria-label', ariaLabel);
            } else {
              element.removeAttribute('aria-label');
            }
          }
        });
      });
    }
  };
  var renderHeader = function renderHeader() {
    var results = [];
    resultsRef.current = [];
    tabsRef.current = [];
    var unfolded = [];
    if (children) {
      var _children = children instanceof Array ? children : [children];
      if (overflowMode) {
        unfolded = _children.slice(0, overflowNumber);
      } else {
        unfolded = _children;
      }
    }
    unfolded && React.Children.forEach(unfolded, function (child, index) {
      if (!(child !== null && child !== void 0 && child.props)) {
        return;
      }
      var _child$props = child.props,
        label = _child$props.label,
        icon = _child$props.icon,
        disabled = _child$props.disabled,
        tooltipOptions = _child$props.tooltipOptions,
        propTabId = _child$props.tabId;
      var tabId = String.fromCharCode(65 + Math.floor(Math.random() * 26)) + Date.now();
      tabIds[index] = "panel" + (label ? '-' + label : '') + "-" + tabId;
      label = getText(label);
      var labelInfo = child.props.label;
      var negatived = child.props.negatived;
      var classToggleNames = pinActiveTab && responsive ? ClassNames('ap-tabset-toggle', {
        'has-label': label,
        'has-icon': icon,
        disabled: disabled
      }) : ClassNames('ap-tabset-toggle', {
        'has-label': label,
        'has-icon': icon,
        disabled: disabled,
        active: curActiveIndex === index,
        'negatived': negatived
      });
      var classIconNames = ClassNames('Appkit4-icon', icon);
      if (!pinActiveTab || pinActiveTab && getText(activeItem === null || activeItem === void 0 ? void 0 : activeItem.label) !== label) {
        var tabIndexInfo = null;
        var ariaSelectedInfo = null;
        if (pinActiveTab) {
          tabIndexInfo = !disabled ? 0 : -1;
          ariaSelectedInfo = false;
        } else {
          tabIndexInfo = curActiveIndex === index && !disabled || overflowMode && curActiveIndex >= overflowNumber && index === 0 ? 0 : -1;
          ariaSelectedInfo = curActiveIndex === index ? true : false;
        }
        results.push( /*#__PURE__*/React.createElement(Tooltip, _extends({
          target: '.ap-progress-stepper-btn-tooltip-0',
          key: index,
          disabled: !tooltipOptions ? true : false
        }, tooltipOptions), /*#__PURE__*/React.createElement("div", {
          key: index,
          id: propTabId || "tab" + (label ? '-' + fillSpace(label) : icon ? icon : '') + "-" + tabId,
          className: classToggleNames,
          onKeyDown: function onKeyDown(e) {
            _onKeyDown(e, child.props);
          },
          onClick: function onClick(e) {
            return selectTab(e, index, disabled, child.props, -1);
          },
          role: "tab",
          "aria-controls": "panel" + (label ? '-' + label : '') + "-" + tabId,
          "aria-selected": ariaSelectedInfo,
          tabIndex: tabIndexInfo,
          "aria-disabled": disabled
          //aria-describedby={`tab-tooltip${label ? '-' + label : ''}-${tabsetId}`}
          // aria-label={!label && icon && typeof icon === 'string' ? icon : ''}
        }, type === 'underline' && /*#__PURE__*/React.createElement("span", {
          className: "ap-tabset-toggle-underline"
        }), /*#__PURE__*/React.createElement("span", {
          className: "ap-tabset-toggle-inactive"
        }, icon && (typeof icon === 'string' ? /*#__PURE__*/React.createElement("span", {
          className: classIconNames,
          "aria-hidden": true
        }) : icon), /*#__PURE__*/React.createElement("span", null, labelInfo)), /*#__PURE__*/React.createElement("span", {
          className: "ap-tabset-toggle-active"
        }, icon && (typeof icon === 'string' ? /*#__PURE__*/React.createElement("span", {
          className: classIconNames,
          "aria-hidden": true
        }) : icon), /*#__PURE__*/React.createElement("span", null, labelInfo)))));
        resultsRef.current.push({
          label: label,
          icon: icon,
          disabled: disabled,
          tooltipOptions: tooltipOptions,
          tabId: tabsetId
        });
      }
      tabsRef.current.push({
        label: label,
        icon: icon,
        disabled: disabled,
        tooltipOptions: tooltipOptions,
        tabId: tabsetId
      });
    });
    return results;
  };
  var renderContent = function renderContent() {
    var results = [];
    React.Children.forEach(children, function (child, index) {
      if (!child && !child.props) {
        return;
      }
      var label = child.props.label;
      label = getText(label);
      var active = curActiveIndex === index;
      results.push( /*#__PURE__*/React.cloneElement(child, _extends({
        active: active,
        label: label,
        key: index,
        ariaId: tabIds[index]
      }, child.props)));
    });
    return results;
  };
  var renderOverflow = function renderOverflow() {
    if (overflowMode) {
      var folded = [];
      if (children && Array.isArray(children)) {
        folded = children.slice(overflowNumber);
      }
      if (pinActiveTab) {
        folded = folded.filter(function (item) {
          return item.props.label !== getText(activeItem === null || activeItem === void 0 ? void 0 : activeItem.label);
        });
      }
      var data = folded.map(function (item, i) {
        return {
          index: overflowNumber + i,
          label: item.props.label,
          value: item.props.label,
          disabled: item.props.disabled,
          childProps: item.props,
          active: curActiveIndex === overflowNumber + i
        };
      });
      return /*#__PURE__*/React.createElement(Tooltip, {
        target: '.ap-progress-stepper-btn-tooltip-0',
        disabled: true,
        key: "overflow"
      }, /*#__PURE__*/React.createElement("div", {
        className: "ap-tabset-toggle overflow-button " + (curActiveIndex >= overflowNumber && !pinActiveTab ? 'active' : '')
      }, /*#__PURE__*/React.createElement("span", {
        className: curActiveIndex >= overflowNumber && !pinActiveTab ? 'ap-tabset-toggle-active' : 'ap-tabset-toggle-inactive'
      }, /*#__PURE__*/React.createElement(DropdownButton, {
        splitButton: false,
        customTriggerNode: true,
        data: data,
        ariaLabel: t('aria').tabsToggleButtonLabel,
        itemTemplate: function itemTemplate(label, item) {
          var _item$childProps;
          return /*#__PURE__*/React.createElement("div", {
            className: "ap-option-left overflow-options"
          }, (item === null || item === void 0 ? void 0 : (_item$childProps = item.childProps) === null || _item$childProps === void 0 ? void 0 : _item$childProps.icon) && /*#__PURE__*/React.createElement("span", {
            className: "Appkit4-icon " + item.childProps.icon
          }), /*#__PURE__*/React.createElement("label", {
            className: "ap-option-label"
          }, label));
        },
        onSelect: function onSelect(value, item, e) {
          selectTab(e, item.index, item.disabled, item.childProps, -1);
        }
      }, /*#__PURE__*/React.createElement("span", {
        className: "Appkit4-icon icon-vertical-more-outline"
      })))));
    } else {
      return /*#__PURE__*/React.createElement(React.Fragment, null);
    }
  };
  var initTabLabelPosition = function initTabLabelPosition() {
    var _tabRef$current4;
    tabLabelPosition.current = [];
    var tabItems = (_tabRef$current4 = tabRef.current) === null || _tabRef$current4 === void 0 ? void 0 : _tabRef$current4.querySelectorAll('.ap-tabset-toggle');
    tabItems && Array.from(tabItems).forEach(function (tabLabel) {
      tabLabelPosition.current.push(tabLabel.offsetLeft);
    });
  };
  var initResponsiveTab = function initResponsiveTab() {
    var _responsiveTab$curren2, _responsiveTab$curren3, _responsiveTab$curren4, _responsiveTab$curren5, _tabRef$current5;
    var leftCtrl = (_responsiveTab$curren2 = responsiveTab.current) === null || _responsiveTab$curren2 === void 0 ? void 0 : _responsiveTab$curren2.querySelector('.ap-tab-move-ctrl.icon-left-chevron-outline');
    var rightCtrl = (_responsiveTab$curren3 = responsiveTab.current) === null || _responsiveTab$curren3 === void 0 ? void 0 : _responsiveTab$curren3.querySelector('.ap-tab-move-ctrl.icon-right-chevron-outline');
    var ctrls = (_responsiveTab$curren4 = responsiveTab.current) === null || _responsiveTab$curren4 === void 0 ? void 0 : _responsiveTab$curren4.querySelectorAll('.ap-tab-move-ctrl');
    var pinnedTab = (_responsiveTab$curren5 = responsiveTab.current) === null || _responsiveTab$curren5 === void 0 ? void 0 : _responsiveTab$curren5.querySelector('.ap-tabset-toggle.pinned');
    ctrls && ctrls.forEach(function (ctrl) {
      ctrl.style.display = 'none';
    });
    var wi = 0;
    var tabItems = (_tabRef$current5 = tabRef.current) === null || _tabRef$current5 === void 0 ? void 0 : _tabRef$current5.querySelectorAll('.ap-tabset-toggle');
    tabItems && Array.from(tabItems).forEach(function (tab) {
      wi += tab.getBoundingClientRect().width + 24;
    });
    wi = wi - 24;
    var isMove = false;
    var container = responsiveTab.current;
    if (pinActiveTab && pinnedTab) {
      wi = wi + pinnedTab.getBoundingClientRect().width;
    } else {
      wi = wi - 24; // width - extra column gap
    }

    if (container) isMove = wi >= container.getBoundingClientRect().width;
    if (isMove) {
      setAllowStrech(false);
      if (arrowLeft && leftCtrl) {
        leftCtrl.style.display = 'block';
      }
      if (arrowRight && rightCtrl) {
        rightCtrl.style.display = 'block';
      }
    } else {
      setAllowStrech(true);
    }
  };
  var setController = function setController() {
    var _responsiveTab$curren6, _responsiveTab$curren7;
    var leftCtrl = (_responsiveTab$curren6 = responsiveTab.current) === null || _responsiveTab$curren6 === void 0 ? void 0 : _responsiveTab$curren6.querySelector('.ap-tab-move-ctrl.icon-left-chevron-outline');
    var rightCtrl = (_responsiveTab$curren7 = responsiveTab.current) === null || _responsiveTab$curren7 === void 0 ? void 0 : _responsiveTab$curren7.querySelector('.ap-tab-move-ctrl.icon-right-chevron-outline');
    if (arrowLeft && arrowRight) {
      leftCtrlDirection.current = 'left';
      rightCtrlDirection.current = 'right';
      if (leftCtrl) {
        leftCtrl.classList.remove('rotate');
      }
      if (rightCtrl) {
        rightCtrl.classList.remove('rotate');
      }
      if (reachBoundary() === 'left') {
        if (leftCtrl) {
          leftCtrl.classList.add('disabled');
          leftCtrl.setAttribute('tabindex', '-1');
        }
        if (rightCtrl) {
          rightCtrl.classList.remove('disabled');
          rightCtrl.setAttribute('tabindex', '0');
        }
      } else if (reachBoundary() === 'right') {
        if (leftCtrl) {
          leftCtrl.classList.remove('disabled');
          leftCtrl.setAttribute('tabindex', '0');
        }
        if (rightCtrl) {
          rightCtrl.classList.add('disabled');
          rightCtrl.setAttribute('tabindex', '-1');
        }
      } else {
        if (leftCtrl) {
          leftCtrl.classList.remove('disabled');
          leftCtrl.setAttribute('tabindex', '0');
        }
        if (rightCtrl) {
          rightCtrl.classList.remove('disabled');
          rightCtrl.setAttribute('tabindex', '0');
        }
      }
      return;
    }
    if (reachBoundary() === 'left') {
      // If reaching the left boundary, set both arrows to the right
      leftCtrlDirection.current = 'right';
      rightCtrlDirection.current = 'right';
      if (leftCtrl) {
        leftCtrl.classList.add('rotate');
      }
      if (rightCtrl) {
        rightCtrl.classList.remove('rotate');
      }
    } else if (reachBoundary() === 'right') {
      // If reaching the right boundary, set both arrows to the left
      leftCtrlDirection.current = 'left';
      rightCtrlDirection.current = 'left';
      if (leftCtrl) {
        leftCtrl.classList.remove('rotate');
      }
      if (rightCtrl) {
        rightCtrl.classList.add('rotate');
      }
    }
  };
  var reachBoundary = function reachBoundary() {
    var _tabRef$current6, _scrollContainer$curr2;
    if (scrollDistance.current - 2 <= 0) {
      return 'left';
    }
    if (scrollDistance.current + 2 >= ((_tabRef$current6 = tabRef.current) === null || _tabRef$current6 === void 0 ? void 0 : _tabRef$current6.offsetWidth) - ((_scrollContainer$curr2 = scrollContainer.current) === null || _scrollContainer$curr2 === void 0 ? void 0 : _scrollContainer$curr2.offsetWidth)) {
      return 'right';
    }
    return 'none';
  };
  var onClickLeft = function onClickLeft(event) {
    event.preventDefault();
    if (arrowLeft && arrowRight) {
      scrollTab('left');
    } else {
      scrollTab(leftCtrlDirection.current);
    }
  };
  var onClickRight = function onClickRight(event) {
    event.preventDefault();
    if (arrowLeft && arrowRight) {
      scrollTab('right');
    } else {
      scrollTab(rightCtrlDirection.current);
    }
  };
  var scrollTab = function scrollTab(direction) {
    var _tabRef$current7;
    var tabList = (_tabRef$current7 = tabRef.current) === null || _tabRef$current7 === void 0 ? void 0 : _tabRef$current7.querySelectorAll('.ap-tabset-toggle');
    var speedValue = speed ? speed : tabRef.current.offsetWidth / tabList.length;
    var move = direction === 'left' ? scrollDistance.current - speedValue : scrollDistance.current + speedValue;
    scrollContainer.current.scroll({
      left: move,
      top: 0,
      behavior: 'smooth'
    });
  };
  var classContainerNames = ClassNames("ap-tabset-container", className);
  var classWrapperNames = ClassNames("ap-tabset-toggle-wrapper", (_ClassNames = {}, _ClassNames["" + type] = type, _ClassNames['stretched'] = allowStrech && stretched, _ClassNames['pinned'] = pinActiveTab, _ClassNames));
  var classOverflowNames = ClassNames('ap-tabset-toggle-wrapper', (_ClassNames2 = {}, _ClassNames2["" + type] = type, _ClassNames2));
  var classResponsiveWrapper = ClassNames('ap-tabset-responsive-wrapper', {
    underline: type === 'underline',
    filled: type === 'filled'
  });
  return /*#__PURE__*/React.createElement("div", {
    ref: ref,
    className: classContainerNames,
    style: style,
    id: id || "tabset-" + tabsetId
  }, responsive ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: classResponsiveWrapper,
    ref: responsiveTab
  }, arrowLeft && type === 'underline' && /*#__PURE__*/React.createElement("span", {
    className: "ap-tab-move-ctrl Appkit4-icon icon-left-chevron-outline",
    tabIndex: 0,
    role: "button",
    onKeyDown: onkeydownLeft,
    onClick: onClickLeft,
    "aria-label": (_clickToScrollLabel = t('aria').clickToScrollLabel) === null || _clickToScrollLabel === void 0 ? void 0 : _clickToScrollLabel.replace("{direction}", leftCtrlDirection.current === 'left' ? t('left') : t('right'))
  }), pinActiveTab && activeItem && /*#__PURE__*/React.createElement("div", {
    className: ClassNames('ap-tabset-toggle-wrapper', type)
  }, /*#__PURE__*/React.createElement("div", {
    className: ClassNames('ap-tabset-toggle active pinned', type, {
      'has-icon': activeItem.icon,
      'has-label': getText(activeItem.label)
    }),
    "aria-live": "assertive",
    "aria-label": (_selected3 = t('aria').selected) === null || _selected3 === void 0 ? void 0 : _selected3.replace("{item}", getText(activeItem.label)),
    "aria-controls": "tabpanel" + (getText(activeItem.label) ? '-' + getText(activeItem.label) : activeItem.icon ? activeItem.icon : ''),
    id: "tab" + (getText(activeItem.label) ? '-' + getText(activeItem.label) : activeItem.icon ? activeItem.icon : ''),
    tabIndex: -1,
    role: 'tab'
  }, type === 'underline' && /*#__PURE__*/React.createElement("span", {
    className: "ap-tabset-toggle-underline"
  }), /*#__PURE__*/React.createElement("span", {
    className: "ap-tabset-toggle-active"
  }, type === 'filled' && activeItem.icon && /*#__PURE__*/React.createElement("span", {
    className: "Appkit4-icon " + activeItem.icon,
    "aria-hidden": true
  }), /*#__PURE__*/React.createElement("span", null, getText(activeItem.label))))), /*#__PURE__*/React.createElement("div", {
    className: "ap-tabset-responsive-scroll-container",
    ref: scrollContainer
  }, /*#__PURE__*/React.createElement("div", {
    ref: tabRef,
    className: classWrapperNames,
    role: "tablist",
    "aria-label": t('aria').tablistLabel
  }, type === 'underline' && /*#__PURE__*/React.createElement("span", {
    className: "ap-tabset-underline"
  }), renderHeader())), overflow && /*#__PURE__*/React.createElement("div", {
    className: classOverflowNames
  }, renderOverflow()), arrowRight && type === 'underline' && /*#__PURE__*/React.createElement("span", {
    className: "ap-tab-move-ctrl Appkit4-icon icon-right-chevron-outline",
    tabIndex: 0,
    role: "button",
    onKeyDown: onkeydownRight,
    onClick: onClickRight,
    "aria-label": (_clickToScrollLabel2 = t('aria').clickToScrollLabel) === null || _clickToScrollLabel2 === void 0 ? void 0 : _clickToScrollLabel2.replace("{direction}", rightCtrlDirection.current === 'left' ? t('left') : t('right'))
  })), /*#__PURE__*/React.createElement("div", {
    className: "ap-tabset-content"
  }, renderContent())) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    ref: tabRef,
    className: classWrapperNames,
    role: "tablist",
    "aria-label": t('aria').tablistLabel
  }, type === 'underline' && /*#__PURE__*/React.createElement("span", {
    className: "ap-tabset-underline"
  }), renderHeader(), renderOverflow()), /*#__PURE__*/React.createElement("div", {
    className: "ap-tabset-content"
  }, renderContent())));
});
export default Tabs;